import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import BASE_URL from '../../url';
import { Link } from 'react-router-dom';
import {
  FiX,
  FiSearch,
  FiTrendingUp,
  FiUserCheck,
  FiXCircle,
  FiLayers,
  FiEye,
  FiUsers,
  FiAward,
  FiFileText,
  FiPlayCircle,
  FiCheckSquare,
  FiCheckCircle,
  FiPauseCircle,
  FiExternalLink
} from 'react-icons/fi';
import '../styles/ManagerDashboard.css';
import '../styles/Submissions.css';

// ─────────────────────────────────────────────────────────────
// Pipeline Tabs Definition (12 Cards Match)
// ─────────────────────────────────────────────────────────────
const PIPELINE_TABS = [
  { key: 'ALL', label: 'All Sourced', icon: <FiUserCheck size={13} />, color: '#4338ca', bg: '#e0e7ff' },
  { key: 'OUTCOME', label: 'Hold / Rej / Withdrawn', icon: <FiXCircle size={13} />, color: '#b91c1c', bg: '#fee2e2' },
  { key: 'SOURCED', label: 'Pre-Client Pool', icon: <FiLayers size={13} />, color: '#7c3aed', bg: '#ede9fe' },
  { key: 'SUBMISSIONS', label: 'Direct Submissions', icon: <FiTrendingUp size={13} />, color: '#0f766e', bg: '#ccfbf1' },
  { key: 'CLIENT_REVIEW', label: 'Client Review', icon: <FiEye size={13} />, color: '#4338ca', bg: '#e0e7ff' },
  { key: 'CLIENT_INTERVIEW_1', label: 'Interview 1', icon: <FiUserCheck size={13} />, color: '#2563eb', bg: '#dbeafe' },
  { key: 'CLIENT_INTERVIEW_2', label: 'Interview 2', icon: <FiUsers size={13} />, color: '#7c3aed', bg: '#ede9fe' },
  { key: 'CLIENT_FINAL_ROUND', label: 'Final Round', icon: <FiAward size={13} />, color: '#d97706', bg: '#fef3c7' },
  { key: 'OFFER', label: 'Offer', icon: <FiFileText size={13} />, color: '#e11d48', bg: '#ffe4e6' },
  { key: 'ONBOARDING_INITIATED', label: 'Onboard Init', icon: <FiPlayCircle size={13} />, color: '#0891b2', bg: '#cffafe' },
  { key: 'ONBOARDING_COMPLETED', label: 'Onboard Done', icon: <FiCheckSquare size={13} />, color: '#059669', bg: '#d1fae5' },
  { key: 'HIRED', label: 'Hired', icon: <FiCheckCircle size={13} />, color: '#16a34a', bg: '#dcfce7' },
];

const matchesFilter = (candidate, filterKey, outcomeSubFilter = 'ALL') => {
  if (!filterKey || filterKey === 'ALL') return true;

  if (filterKey === 'OUTCOME') {
    if (candidate.pipelineCategory !== 'OUTCOME') return false;
    if (!outcomeSubFilter || outcomeSubFilter === 'ALL') return true;
    if (outcomeSubFilter === 'HOLD') return candidate.outcomeType === 'HOLD';
    if (outcomeSubFilter === 'REJECTED') return candidate.outcomeType === 'REJECTED';
    if (outcomeSubFilter === 'WITHDRAWN') return candidate.outcomeType === 'WITHDRAWN';
    return true;
  }

  if (filterKey === 'SOURCED') {
    return candidate.pipelineCategory === 'SOURCED';
  }

  if (filterKey === 'SUBMISSIONS') {
    return candidate.pipelineCategory === 'SUBMISSIONS';
  }

  const code = (candidate.stageCode || '').toUpperCase();
  const status = (candidate.submissionStatus || '').toUpperCase();

  if (filterKey === 'HIRED') {
    return code === 'HIRED' || code === 'PLACED' || status === 'HIRED' || status === 'PLACED';
  }
  if (filterKey === 'ONBOARDING_COMPLETED') {
    return code === 'ONBOARDING_COMPLETED';
  }
  if (filterKey === 'ONBOARDING_INITIATED') {
    return code === 'ONBOARDING_INITIATED';
  }
  if (filterKey === 'OFFER') {
    return code === 'OFFER' || code === 'OFFER_ACCEPTED' || code.includes('OFFER');
  }
  if (filterKey === 'CLIENT_FINAL_ROUND') {
    return code === 'CLIENT_FINAL_ROUND';
  }
  if (filterKey === 'CLIENT_INTERVIEW_2') {
    return code === 'CLIENT_INTERVIEW_2';
  }
  if (filterKey === 'CLIENT_INTERVIEW_1') {
    return code === 'CLIENT_INTERVIEW_1';
  }
  if (filterKey === 'CLIENT_REVIEW') {
    return (
      code === 'CLIENT_REVIEW' ||
      code === 'SUBMITTED_TO_CLIENT' ||
      (candidate.pipelineCategory === 'SUBMISSIONS' &&
        !['HIRED', 'PLACED', 'ONBOARDING_COMPLETED', 'ONBOARDING_INITIATED', 'OFFER', 'OFFER_ACCEPTED', 'CLIENT_FINAL_ROUND', 'CLIENT_INTERVIEW_2', 'CLIENT_INTERVIEW_1'].includes(code))
    );
  }

  return code === filterKey;
};

const getStageBadgeStyle = (candidate) => {
  const code = (candidate.stageCode || '').toUpperCase();
  const outcomeType = candidate.outcomeType || '';

  if (candidate.pipelineCategory === 'OUTCOME') {
    if (outcomeType === 'HOLD' || code.includes('HOLD')) {
      return {
        bg: '#fef3c7',
        color: '#b45309',
        border: '1px solid #fde68a',
        label: 'ON HOLD',
        icon: <FiPauseCircle size={12} style={{ marginRight: '4px' }} />
      };
    }
    if (outcomeType === 'WITHDRAWN' || code.includes('WITHDRAW')) {
      return {
        bg: '#ffe4e6',
        color: '#e11d48',
        border: '1px solid #fecdd3',
        label: 'WITHDRAWN',
        icon: <FiXCircle size={12} style={{ marginRight: '4px' }} />
      };
    }
    return {
      bg: '#fee2e2',
      color: '#b91c1c',
      border: '1px solid #fecaca',
      label: 'REJECTED',
      icon: <FiXCircle size={12} style={{ marginRight: '4px' }} />
    };
  }

  if (candidate.pipelineCategory === 'SOURCED') {
    return {
      bg: '#ede9fe',
      color: '#6d28d9',
      border: '1px solid #ddd6fe',
      label: code ? code.replace(/_/g, ' ') : 'PRE-CLIENT POOL',
      icon: <FiLayers size={12} style={{ marginRight: '4px' }} />
    };
  }

  // Client Stage Badges
  if (code === 'HIRED' || code === 'PLACED') {
    return { bg: '#dcfce7', color: '#15803d', border: '1px solid #86efac', label: 'HIRED', icon: <FiCheckCircle size={12} style={{ marginRight: '4px' }} /> };
  }
  if (code === 'ONBOARDING_COMPLETED') {
    return { bg: '#d1fae5', color: '#047857', border: '1px solid #a7f3d0', label: 'ONBOARD DONE', icon: <FiCheckSquare size={12} style={{ marginRight: '4px' }} /> };
  }
  if (code === 'ONBOARDING_INITIATED') {
    return { bg: '#cffafe', color: '#0e7490', border: '1px solid #a5f3fc', label: 'ONBOARD INIT', icon: <FiPlayCircle size={12} style={{ marginRight: '4px' }} /> };
  }
  if (code.includes('OFFER')) {
    return { bg: '#ffe4e6', color: '#be123c', border: '1px solid #fecdd3', label: 'OFFER', icon: <FiFileText size={12} style={{ marginRight: '4px' }} /> };
  }
  if (code === 'CLIENT_FINAL_ROUND') {
    return { bg: '#fef3c7', color: '#b45309', border: '1px solid #fde68a', label: 'FINAL ROUND', icon: <FiAward size={12} style={{ marginRight: '4px' }} /> };
  }
  if (code === 'CLIENT_INTERVIEW_2') {
    return { bg: '#ede9fe', color: '#6d28d9', border: '1px solid #ddd6fe', label: 'INTERVIEW 2', icon: <FiUsers size={12} style={{ marginRight: '4px' }} /> };
  }
  if (code === 'CLIENT_INTERVIEW_1') {
    return { bg: '#dbeafe', color: '#1d4ed8', border: '1px solid #bfdbfe', label: 'INTERVIEW 1', icon: <FiUserCheck size={12} style={{ marginRight: '4px' }} /> };
  }
  return { bg: '#e0e7ff', color: '#3730a3', border: '1px solid #c7d2fe', label: 'CLIENT REVIEW', icon: <FiEye size={12} style={{ marginRight: '4px' }} /> };
};

const SourcingPipelineModal = ({
  startDate,
  endDate,
  region,
  client,
  initialFilter = 'ALL',
  onClose
}) => {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilterKey, setActiveFilterKey] = useState(initialFilter || 'ALL');
  const [outcomeSubFilter, setOutcomeSubFilter] = useState('ALL'); // 'ALL' | 'HOLD' | 'REJECTED' | 'WITHDRAWN'

  // Sync initialFilter changes
  useEffect(() => {
    setActiveFilterKey(initialFilter || 'ALL');
  }, [initialFilter]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Fetch all pipeline candidates
  useEffect(() => {
    const fetchPipelineCandidates = async () => {
      try {
        setLoading(true);
        setError('');
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${BASE_URL}/api/recruitment/applications/job-report/pipeline-candidates`,
          {
            params: {
              startDate,
              endDate,
              region: region || 'onsite',
              client: client ? (typeof client === 'object' ? client.name : client) : 'all'
            },
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        setCandidates(response.data || []);
      } catch (err) {
        console.error('Error fetching sourcing pipeline candidates:', err);
        setError('Failed to load pipeline candidates.');
      } finally {
        setLoading(false);
      }
    };

    fetchPipelineCandidates();
  }, [startDate, endDate, region, client]);

  // Tab counts calculation
  const tabCounts = useMemo(() => {
    const counts = { ALL: candidates.length };
    PIPELINE_TABS.forEach((tab) => {
      if (tab.key !== 'ALL') {
        counts[tab.key] = candidates.filter((c) => matchesFilter(c, tab.key, 'ALL')).length;
      }
    });
    return counts;
  }, [candidates]);

  // Outcome sub-filter counts (Hold, Rejected, Withdrawn)
  const outcomeCounts = useMemo(() => {
    const outcomeList = candidates.filter((c) => c.pipelineCategory === 'OUTCOME');
    return {
      ALL: outcomeList.length,
      HOLD: outcomeList.filter((c) => c.outcomeType === 'HOLD').length,
      REJECTED: outcomeList.filter((c) => c.outcomeType === 'REJECTED').length,
      WITHDRAWN: outcomeList.filter((c) => c.outcomeType === 'WITHDRAWN').length
    };
  }, [candidates]);

  // Client-side filtering
  const filteredCandidates = useMemo(() => {
    let list = candidates.filter((c) => matchesFilter(c, activeFilterKey, outcomeSubFilter));
    if (!searchQuery.trim()) return list;

    const q = searchQuery.toLowerCase().trim();
    return list.filter((item) => {
      const name = (item.candidateName || '').toLowerCase();
      const email = (item.candidateEmail || '').toLowerCase();
      const phone = (item.candidatePhone || '').toLowerCase();
      const recruiter = (item.recruiterName || '').toLowerCase();
      const clientName = (item.clientName || '').toLowerCase();
      const jobTitle = (item.jobTitle || '').toLowerCase();
      const jobCode = (item.jobCode || '').toLowerCase();
      const externalRef = (item.externalJobRef || '').toLowerCase();
      const stage = (item.stageCode || '').toLowerCase();

      return (
        name.includes(q) ||
        email.includes(q) ||
        phone.includes(q) ||
        recruiter.includes(q) ||
        clientName.includes(q) ||
        jobTitle.includes(q) ||
        jobCode.includes(q) ||
        externalRef.includes(q) ||
        stage.includes(q)
      );
    });
  }, [candidates, activeFilterKey, outcomeSubFilter, searchQuery]);

  // Date formatters
  const formatDateRange = () => {
    if (!startDate && !endDate) return 'All Dates';
    const s = startDate ? new Date(startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
    const e = endDate ? new Date(endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
    return s && e ? `${s} — ${e}` : (s || e);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const clientDisplayName = client ? (typeof client === 'object' ? client.name : client) : 'All Clients';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-panel"
        style={{
          maxWidth: '1300px',
          width: '96%',
          height: '88vh',
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '12px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          overflow: 'hidden'
        }}
        onClick={(ev) => ev.stopPropagation()}
      >
        {/* Header */}
        <div
          className="modal-header"
          style={{
            background: 'linear-gradient(135deg, #f0f7ff 0%, #eff6ff 100%)',
            borderBottom: '1px solid #bfdbfe',
            padding: '16px 24px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0
          }}
        >
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  background: '#ccfbf1',
                  color: '#0f766e'
                }}
              >
                <FiTrendingUp size={18} />
              </span>
              <div>
                <h3 className="modal-title" style={{ color: '#0f766e', margin: 0, fontSize: '18px', fontWeight: 700 }}>
                  Sourcing &amp; Direct Submissions Pipeline ({candidates.length} Total Candidates)
                </h3>
                <p className="modal-subtitle" style={{ color: '#134e4a', marginTop: '4px', fontSize: '12px', margin: 0 }}>
                  Period: <strong>{formatDateRange()}</strong> &bull; Region: <strong style={{ textTransform: 'capitalize' }}>{region || 'Onsite'}</strong> &bull; Client: <strong>{clientDisplayName}</strong>
                </p>
              </div>
            </div>
          </div>
          <button
            className="modal-close-btn"
            onClick={onClose}
            aria-label="Close"
            style={{
              background: '#ffffff',
              border: '1px solid #ccfbf1',
              color: '#374151',
              borderRadius: '6px',
              padding: '6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <FiX size={18} />
          </button>
        </div>

        {/* 12 Filter Tabs (Scrollable Bar) */}
        <div
          style={{
            display: 'flex',
            gap: '6px',
            overflowX: 'auto',
            padding: '10px 20px',
            background: '#ffffff',
            borderBottom: '1px solid #f1f5f9',
            flexShrink: 0,
            scrollbarWidth: 'thin'
          }}
        >
          {PIPELINE_TABS.map((tab) => {
            const count = tabCounts[tab.key] || 0;
            const isActive = activeFilterKey === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => {
                  setActiveFilterKey(tab.key);
                  if (tab.key !== 'OUTCOME') setOutcomeSubFilter('ALL');
                }}
                style={{
                  padding: '5px 12px',
                  borderRadius: '20px',
                  border: isActive ? `1.5px solid ${tab.color}` : '1px solid #e2e8f0',
                  background: isActive ? tab.color : '#f8fafc',
                  color: isActive ? '#ffffff' : '#475569',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  flexShrink: 0,
                  height: '32px',
                  transition: 'all 0.15s ease'
                }}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
                <span
                  style={{
                    background: isActive ? 'rgba(255, 255, 255, 0.25)' : '#e2e8f0',
                    color: isActive ? '#ffffff' : '#334155',
                    borderRadius: '10px',
                    padding: '1px 6px',
                    fontSize: '11px',
                    fontWeight: 700
                  }}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Outcome Sub-Filters (When OUTCOME Tab is Selected) */}
        {activeFilterKey === 'OUTCOME' && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 20px',
              background: '#fef2f2',
              borderBottom: '1px solid #fecaca',
              flexShrink: 0
            }}
          >
            <span style={{ fontSize: '12px', fontWeight: 700, color: '#991b1b', marginRight: '4px' }}>Filter Outcome:</span>
            {[
              { key: 'ALL', label: `All Outcome (${outcomeCounts.ALL})` },
              { key: 'HOLD', label: `⏸️ On Hold (${outcomeCounts.HOLD})` },
              { key: 'REJECTED', label: `❌ Rejected (${outcomeCounts.REJECTED})` },
              { key: 'WITHDRAWN', label: `↩️ Withdrawn (${outcomeCounts.WITHDRAWN})` }
            ].map((sub) => {
              const isSubActive = outcomeSubFilter === sub.key;
              return (
                <button
                  key={sub.key}
                  onClick={() => setOutcomeSubFilter(sub.key)}
                  style={{
                    padding: '4px 12px',
                    borderRadius: '14px',
                    border: isSubActive ? '1px solid #b91c1c' : '1px solid #fca5a5',
                    background: isSubActive ? '#b91c1c' : '#ffffff',
                    color: isSubActive ? '#ffffff' : '#7f1d1d',
                    fontSize: '11.5px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    flexShrink: 0,
                    transition: 'all 0.15s ease'
                  }}
                >
                  {sub.label}
                </button>
              );
            })}
          </div>
        )}

        {/* Search Bar & Counter Strip */}
        <div
          style={{
            padding: '10px 20px',
            background: '#f8fafc',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '16px',
            flexShrink: 0
          }}
        >
          <div style={{ position: 'relative', flex: 1, maxWidth: '420px' }}>
            <FiSearch
              size={15}
              style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}
            />
            <input
              type="text"
              placeholder="Search candidate, job, recruiter, client..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px 8px 36px',
                borderRadius: '6px',
                border: '1px solid #cbd5e1',
                fontSize: '13px',
                outline: 'none',
                background: '#ffffff',
                boxSizing: 'border-box'
              }}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#94a3b8',
                  padding: '2px'
                }}
              >
                <FiX size={14} />
              </button>
            )}
          </div>

          <span style={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>
            Showing <strong>{filteredCandidates.length}</strong> of <strong>{candidates.length}</strong> candidates
          </span>
        </div>

        {/* Table Body */}
        <div className="modal-body" style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 0 }}>
          {loading ? (
            <div className="modal-loading" style={{ padding: '60px', textAlign: 'center' }}>
              <div className="modal-spinner" />
              <p style={{ marginTop: '12px', color: '#64748b' }}>Loading pipeline candidates...</p>
            </div>
          ) : error ? (
            <div className="modal-error" style={{ margin: '20px', padding: '16px', background: '#fee2e2', color: '#b91c1c', borderRadius: '8px' }}>
              {error}
            </div>
          ) : filteredCandidates.length === 0 ? (
            <div className="modal-empty" style={{ padding: '60px 20px', textAlign: 'center', color: '#94a3b8' }}>
              <FiUserCheck size={36} style={{ marginBottom: '12px', opacity: 0.5 }} />
              <p style={{ fontSize: '15px', fontWeight: 600, margin: '0 0 6px 0', color: '#64748b' }}>
                No candidates found matching the selected filter.
              </p>
              {searchQuery && (
                <p style={{ fontSize: '13px', margin: 0 }}>Try clearing your search query &ldquo;{searchQuery}&rdquo;</p>
              )}
            </div>
          ) : (
            <div className="modal-table-wrapper" style={{ height: '100%', overflowX: 'auto' }}>
              <table className="submissions-table" style={{ fontSize: '13px', width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ position: 'sticky', top: 0, background: '#f8fafc', zIndex: 2 }}>Candidate</th>
                    <th style={{ position: 'sticky', top: 0, background: '#f8fafc', zIndex: 2 }}>Stage / Status</th>
                    <th style={{ position: 'sticky', top: 0, background: '#f8fafc', zIndex: 2 }}>Recruiter</th>
                    <th style={{ position: 'sticky', top: 0, background: '#f8fafc', zIndex: 2 }}>Client &amp; Job Ref</th>
                    <th style={{ position: 'sticky', top: 0, background: '#f8fafc', zIndex: 2 }}>Job Title</th>
                    <th style={{ position: 'sticky', top: 0, background: '#f8fafc', zIndex: 2 }}>Experience</th>
                    <th style={{ position: 'sticky', top: 0, background: '#f8fafc', zIndex: 2 }}>Location</th>
                    <th style={{ position: 'sticky', top: 0, background: '#f8fafc', zIndex: 2 }}>Pay Rate</th>
                    <th style={{ position: 'sticky', top: 0, background: '#f8fafc', zIndex: 2 }}>Sourced Date</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCandidates.map((row, idx) => {
                    const badge = getStageBadgeStyle(row);
                    return (
                      <tr key={row.submissionId || `${row.id}-${idx}`}>
                        {/* Candidate */}
                        <td>
                          <div style={{ fontWeight: 600, color: '#0f172a' }}>{row.candidateName || 'Candidate'}</div>
                          {row.candidateEmail && row.candidateEmail !== 'N/A' && (
                            <div style={{ fontSize: '11px', color: '#64748b' }}>{row.candidateEmail}</div>
                          )}
                          {row.candidatePhone && row.candidatePhone !== 'N/A' && (
                            <div style={{ fontSize: '10.5px', color: '#94a3b8' }}>{row.candidatePhone}</div>
                          )}
                        </td>

                        {/* Stage Badge */}
                        <td>
                          <span
                            style={{
                              background: badge.bg,
                              color: badge.color,
                              border: badge.border,
                              padding: '3px 8px',
                              borderRadius: '12px',
                              fontSize: '11px',
                              fontWeight: 700,
                              display: 'inline-flex',
                              alignItems: 'center',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {badge.icon}
                            {badge.label}
                          </span>
                        </td>

                        {/* Recruiter */}
                        <td style={{ fontWeight: 500, color: '#334155' }}>
                          {row.recruiterName || 'Unassigned'}
                        </td>

                        {/* Client & Job Ref */}
                        <td>
                          <div style={{ fontWeight: 600, color: '#0f766e' }}>{row.clientName || 'N/A'}</div>
                          <div style={{ fontSize: '11.5px', color: '#0284c7', fontWeight: 500 }}>
                            {row.externalJobRef || row.jobCode || (row.jobId ? `JOB-${row.jobId}` : 'N/A')}
                          </div>
                        </td>

                        {/* Job Title */}
                        <td>
                          <Link
                            to={`/submissions/${row.jobId || row.id}`}
                            state={{ jobInfo: row }}
                            className="job-title-link"
                            onClick={onClose}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                          >
                            <span>{row.jobTitle || 'View Job'}</span>
                            <FiExternalLink size={11} style={{ opacity: 0.6 }} />
                          </Link>
                        </td>

                        {/* Experience */}
                        <td>{row.experience || 'N/A'}</td>

                        {/* Location */}
                        <td
                          className="na-text"
                          style={{ maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          title={row.location}
                        >
                          {row.location || 'N/A'}
                        </td>

                        {/* Rate */}
                        <td style={{ fontWeight: 500, color: '#334155' }}>
                          {row.rate || 'N/A'}
                        </td>

                        {/* Sourced Date */}
                        <td style={{ whiteSpace: 'nowrap', color: '#64748b' }}>
                          {formatDate(row.createdAt)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        {!loading && !error && (
          <div
            className="modal-footer"
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '12px 24px',
              background: '#f8fafc',
              borderTop: '1px solid #e2e8f0',
              flexShrink: 0
            }}
          >
            <span style={{ fontSize: '12.5px', color: '#64748b' }}>
              Showing <strong>{filteredCandidates.length}</strong> candidate{filteredCandidates.length !== 1 ? 's' : ''} &bull; Total pool: <strong>{candidates.length}</strong>
            </span>
            <button
              className="modal-close-footer-btn"
              onClick={onClose}
              style={{
                padding: '6px 20px',
                borderRadius: '6px',
                background: '#0f766e',
                color: '#ffffff',
                border: 'none',
                fontWeight: 600,
                fontSize: '13px',
                cursor: 'pointer'
              }}
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SourcingPipelineModal;
